/**
 * Chicago Manual of Style (17th Edition) citation formats.
 * Implements three styles: Notes, Bibliography, and Author-Date.
 * @see https://www.chicagomanualofstyle.org/tools_citationguide/citation-guide-1.html
 */

import {
    formatContributorInvertedName,
    getContributorFullName,
    getContributorLastName,
    getPageNumber,
    getPublicationYear,
    getPublisher,
    getTitle,
    getValidContributors,
    hasAuthorLastName,
    hasCompleteAuthor,
    hasPublicationYear,
    hasPublisher,
    hasTitle,
} from './citationHelpers.js';

/**
 * Formats contributors for Chicago Notes style.
 * Format: "Name Name" for single, "Name Name and Name Name" for two, "Name, Name, and Name" for three, "Name et al." for 4+
 * @param {Object} tags - Issue tags containing contributors array
 * @returns {string} Formatted contributors string
 */
function getChicagoNotesContributors(tags) {
    const contributors = getValidContributors(tags).map(getContributorFullName);
    if (contributors.length === 0) {
        return '';
    }

    if (contributors.length === 1) {
        return contributors[0];
    }

    if (contributors.length === 2) {
        return `${contributors[0]} and ${contributors[1]}`;
    }

    if (contributors.length === 3) {
        return `${contributors[0]}, ${contributors[1]}, and ${contributors[2]}`;
    }

    return `${contributors[0]} et al.`;
}

/**
 * Formats contributors for Chicago Bibliography style.
 * Format: "Lastname, Firstname" for first contributor, "Firstname Lastname" for others
 * @param {Object} tags - Issue tags containing contributors array
 * @returns {string} Formatted contributors string
 */
function getChicagoBibliographyContributors(tags) {
    const contributors = getValidContributors(tags);
    if (contributors.length === 0) {
        return '';
    }

    if (contributors.length === 1) {
        return formatContributorInvertedName(contributors[0]);
    }

    if (contributors.length === 2) {
        return `${formatContributorInvertedName(contributors[0])}, and ${getContributorFullName(contributors[1])}`;
    }

    if (contributors.length === 3) {
        return `${formatContributorInvertedName(contributors[0])}, ${getContributorFullName(contributors[1])}, and ${getContributorFullName(contributors[2])}`;
    }

    // For 4+ contributors, list all (up to 6, then et al.)
    if (contributors.length <= 6) {
        const formattedFirst = formatContributorInvertedName(contributors[0]);
        const middle = contributors.slice(1, -1).map(getContributorFullName).join(', ');
        const last = getContributorFullName(contributors[contributors.length - 1]);
        return `${formattedFirst}, ${middle}, and ${last}`;
    }

    // More than 6 contributors
    const firstSix = contributors.slice(0, 6);
    const formattedFirst = formatContributorInvertedName(firstSix[0]);
    const middle = firstSix.slice(1).map(getContributorFullName).join(', ');
    return `${formattedFirst}, ${middle}, et al.`;
}

/**
 * Formats contributors for Chicago Author-Date style (only last names).
 * @param {Object} tags - Issue tags containing contributors array
 * @returns {string} Formatted contributors string (last names only)
 */
function getChicagoAuthorDateContributors(tags) {
    const contributors = getValidContributors(tags);
    if (contributors.length === 0) {
        return '';
    }

    if (contributors.length === 1) {
        return getContributorLastName(contributors[0]);
    }

    if (contributors.length === 2) {
        return `${getContributorLastName(contributors[0])} and ${getContributorLastName(contributors[1])}`;
    }

    if (contributors.length === 3) {
        return `${getContributorLastName(contributors[0])}, ${getContributorLastName(contributors[1])}, and ${getContributorLastName(contributors[2])}`;
    }

    return `${getContributorLastName(contributors[0])} et al.`;
}

/**
 * Gets the page number formatted for Chicago citation.
 * @param {number|string|Object} currentLocation - Current page/location
 * @param {boolean} isEpub - Whether this is an EPUB file
 * @param {boolean} includePageNumber - Whether to include page number
 * @returns {string} Page number as string or empty string
 */
function getChicagoPage(currentLocation, isEpub, includePageNumber) {
    const pageNumber = getPageNumber(currentLocation, isEpub, includePageNumber);
    if (pageNumber !== null) {
        return String(pageNumber);
    }
    return '';
}

/**
 * Chicago Notes style citation.
 * Format: Name Name, Title (Publisher, Year), page.
 * @param {string} text - Selected text to cite
 * @param {Object} issue - Issue/publication metadata
 * @param {number|string|Object} currentLocation - Current page/location
 * @param {string} issueTitle - Publication title
 * @param {boolean} isEpub - Whether this is an EPUB file
 * @returns {string} Formatted Chicago Notes citation
 */
export const ChicagoNotesCitation = (text, issue, currentLocation, issueTitle, isEpub) => {
    const tags = issue.tags || {};
    const includePageNumber = text && text.trim().length > 0;

    const authors = getChicagoNotesContributors(tags);
    const title = getTitle(issue, issueTitle);
    const publisher = getPublisher(tags);
    const year = getPublicationYear(issue);
    const page = getChicagoPage(currentLocation, isEpub, includePageNumber);

    const parts = [];

    if (authors) {
        parts.push(authors);
    }

    if (title) {
        parts.push(title);
    }

    let citationBase = parts.join(', ');

    if (publisher || year) {
        const pubYearParts = [];
        if (publisher) pubYearParts.push(publisher);
        if (year) pubYearParts.push(year);
        citationBase += ` (${pubYearParts.join(', ')})`;
    }

    if (page) {
        citationBase += `, ${page}`;
    }

    citationBase += '.';

    if (text && text.trim()) {
        return `"${text}"

${citationBase}`;
    }

    return citationBase;
};

/**
 * Chicago Bibliography style citation.
 * Format: Lastname, Firstname. Title. Publisher, Year.
 * Note: Never includes page number.
 * @param {string} text - Selected text to cite (ignored for page)
 * @param {Object} issue - Issue/publication metadata
 * @param {number|string|Object} currentLocation - Current page/location (ignored)
 * @param {string} issueTitle - Publication title
 * @returns {string} Formatted Chicago Bibliography citation
 */
export const ChicagoBibliographyCitation = (text, issue, currentLocation, issueTitle) => {
    const tags = issue.tags || {};

    const authors = getChicagoBibliographyContributors(tags);
    const title = getTitle(issue, issueTitle);
    const publisher = getPublisher(tags);
    const year = getPublicationYear(issue);

    const parts = [];

    if (authors) {
        parts.push(`${authors}.`);
    }

    if (title) {
        parts.push(`${title}.`);
    }

    if (publisher && year) {
        parts.push(`${publisher}, ${year}.`);
    } else if (publisher) {
        parts.push(`${publisher}.`);
    } else if (year) {
        parts.push(`${year}.`);
    }

    const citation = parts.join(' ');

    if (text && text.trim()) {
        return `"${text}"

${citation}`;
    }

    return citation;
};

/**
 * Chicago Author-Date style citation.
 * Format: (Lastname Year) or (Lastname Year, page)
 * @param {string} text - Selected text to cite
 * @param {Object} issue - Issue/publication metadata
 * @param {number|string|Object} currentLocation - Current page/location
 * @param {string} issueTitle - Publication title (not used)
 * @param {boolean} isEpub - Whether this is an EPUB file
 * @returns {string} Formatted Chicago Author-Date citation
 */
export const ChicagoAuthorDateCitation = (text, issue, currentLocation, issueTitle, isEpub) => {
    const tags = issue.tags || {};
    const includePageNumber = text && text.trim().length > 0;

    const authors = getChicagoAuthorDateContributors(tags);
    const year = getPublicationYear(issue);
    const page = getChicagoPage(currentLocation, isEpub, includePageNumber);

    const citationParts = [];

    if (authors) {
        citationParts.push(authors);
    }

    if (year) {
        citationParts.push(year);
    }

    let citationInner = citationParts.join(' ');

    if (page) {
        citationInner += `, ${page}`;
    }

    const citation = `(${citationInner})`;

    if (text && text.trim()) {
        return `"${text}"

${citation}`;
    }

    return citation;
};

/**
 * Validates that the required metadata for Chicago Notes citation format is available.
 * Requires: complete author, title, publisher, year.
 * @param {Object} issue - Issue object with tags and publication_date
 * @param {string} title - Title from store
 * @returns {boolean} True if Chicago Notes requirements are met
 */
export function validateChicagoNotes(issue, title) {
    return hasCompleteAuthor(issue?.tags) && hasTitle(title) && hasPublisher(issue?.tags) && hasPublicationYear(issue);
}

/**
 * Validates that the required metadata for Chicago Bibliography citation format is available.
 * Requires: complete author, title, publisher, year.
 * @param {Object} issue - Issue object with tags and publication_date
 * @param {string} title - Title from store
 * @returns {boolean} True if Chicago Bibliography requirements are met
 */
export function validateChicagoBibliography(issue, title) {
    return hasCompleteAuthor(issue?.tags) && hasTitle(title) && hasPublisher(issue?.tags) && hasPublicationYear(issue);
}

/**
 * Validates that the required metadata for Chicago Author-Date citation format is available.
 * Requires: contributor with lastName, year.
 * @param {Object} issue - Issue object with tags and publication_date
 * @returns {boolean} True if Chicago Author-Date requirements are met
 */
export function validateChicagoAuthorDate(issue) {
    return hasAuthorLastName(issue?.tags) && hasPublicationYear(issue);
}

/**
 * Validates the Chicago parent format.
 * Chicago is available if at least one subformat is available.
 * Since ChicagoAuthorDate has the minimum requirements (contributor + year),
 * Chicago is shown if ChicagoAuthorDate can be shown.
 * @param {Object} issue - Issue object with tags and publication_date
 * @returns {boolean} True if at least one Chicago subformat is available
 */
export function validateChicago(issue) {
    return validateChicagoAuthorDate(issue);
}

export default {
    Notes: ChicagoNotesCitation,
    Bibliography: ChicagoBibliographyCitation,
    AuthorDate: ChicagoAuthorDateCitation,
};
